import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import DynamicIcon from "./DynamicIcon";
import { getNodeApiUrl } from "../config/api.config";
import { useColorPalette } from "../hooks/useColorPalette";
import { useSelector, useDispatch } from "react-redux";
import { selectAccountData } from "../redux/main/accountSlice";
import { closeModal } from "../redux/rightModalSlice";
import AttachmentsViewer from "./AttachmentsViewer";

/**
 * Componente riutilizzabile per visualizzare i dettagli di un appuntamento
 * Include gestione allegati, note e dettagli di chiusura
 */
export default function AppointmentCard({ appointment, token, apiBaseUrl, selectedAppointmentId }) {
    const [expandedNotes, setExpandedNotes] = useState({});
    const [downloadingFiles, setDownloadingFiles] = useState({});
    const [expandedDetails, setExpandedDetails] = useState({});
    const accountData = useSelector(selectAccountData);
    const palette = useColorPalette();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    // Funzioni per formattazione date
    const formatDate = (date) => {
        if (!date) return "-";
        const d = new Date(date);
        return d.toLocaleDateString("it-IT");
    };
    
    const formatTime = (date) => {
        if (!date) return "-";
        const d = new Date(date);
        return d.toLocaleTimeString("it-IT", { hour: '2-digit', minute: '2-digit' });
    };
    
    const formatDateRange = (fromDate, toDate) => {
        if (!fromDate || !toDate) return "-";
        const from = new Date(fromDate);
        const to = new Date(toDate);
        
        // Se le date sono uguali, mostra solo la data una volta
        if (from.toDateString() === to.toDateString()) {
            return `${from.toLocaleDateString("it-IT")} ${formatTime(from)}-${formatTime(to)}`;
        } else {
            return `${formatDate(from)} ${formatTime(from)} - ${formatDate(to)} ${formatTime(to)}`;
        }
    };
    
    // Gestione note espandibili
    const toggleNotes = (appId, userId) => {
        setExpandedNotes(prev => ({
            ...prev,
            [`${appId}_${userId}`]: !prev[`${appId}_${userId}`]
        }));
    };
    
    // Gestione espansione dettagli
    const toggleDetails = (appId) => {
        setExpandedDetails(prev => ({
            ...prev,
            [appId]: !prev[appId]
        }));
    };
    
    // Gestione download file
    const downloadAllFiles = async (appId, closingDetail) => {
        setDownloadingFiles(prev => ({ ...prev, [`${appId}_${closingDetail.userId}`]: true }));
        try {
            if (!closingDetail.files || closingDetail.files.length === 0) {
                throw new Error('Nessun file valido da scaricare');
            }

            const apiUrl = apiBaseUrl ? 
                `${apiBaseUrl}/files/downloadZip` : 
                getNodeApiUrl('files/downloadZip');
            
            const response = await axios({
                url: apiUrl,
                method: 'GET',
                responseType: 'blob',
                headers: { 'auth-token': token },
                params: {
                    appointmentId: appId,
                    userId: closingDetail.userId
                }
            });
            
            // Crea un URL per il blob e avvia il download
            const fileUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = fileUrl;
            link.setAttribute('download', `allegati-${appId}-${closingDetail.userLabel}.zip`.replace(/\s+/g, '_'));
            document.body.appendChild(link);
            link.click();
            link.remove();
            
        } catch (err) {
            console.error('Errore nel download dei file:', err);
            alert('Errore nel download dei file: ' + (err.message || 'Si è verificato un errore'));
        }
        setDownloadingFiles(prev => ({ ...prev, [`${appId}_${closingDetail.userId}`]: false }));
    };
    
    // Funzioni per gestione file e allegati
    const getFileUrl = (file) => {
        if (!file) return '';
        
        // Se file è un oggetto con URL
        if (typeof file === 'object' && file.url) {
            return file.url;
        }
        
        // A questo punto file dovrebbe essere una stringa
        const fileId = typeof file === 'string' ? file : '';
        if (!fileId) return '';
        
        // Controlla se l'ID del file contiene un'URL completa
        if (fileId.startsWith('http')) {
            return fileId;
        }
        
        // Se l'ID inizia con gs:// è un percorso su Google Cloud Storage
        if (fileId.startsWith('gs://')) {
            // Estraiamo il bucket e il percorso
            const gcsPath = fileId.replace('gs://', '');
            const [bucket, ...objectParts] = gcsPath.split('/');
            const objectPath = objectParts.join('/');
            
            // Creiamo l'URL pubblico di Google Cloud Storage
            return `https://storage.googleapis.com/${bucket}/${objectPath}`;
        }
        
        // URL di base per i file caricati
        const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
        
        // Restituisce l'URL del file
        return `${baseUrl}/uploads/${fileId}`;
    };
    
    const getFileExtension = (file) => {
        if (!file) return '';
        
        // Ottieni l'URL del file
        const fileUrl = getFileUrl(file);
        
        // Per i file di Google Cloud Storage, potrebbero non avere estensione nel nome
        if (fileUrl.includes('storage.googleapis.com')) {
            // Se c'è un parametro che indica il tipo di contenuto
            if (fileUrl.includes('contentType=application/pdf')) {
                return 'pdf';
            }
            // Verifica se l'URL termina con .pdf
            if (fileUrl.toLowerCase().endsWith('.pdf')) {
                return 'pdf';
            }
            // Altri tipi MIME possono essere aggiunti qui
        }
        
        // Ricerca dell'estensione nel nome del file
        const parts = fileUrl.split('.');
        const ext = parts.length > 1 ? parts.pop().toLowerCase() : '';
        
        return ext;
    };
    
    const isImageFile = (file) => {
        const ext = getFileExtension(file);
        return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
    };
    
    const isPdfFile = (file) => {
        const ext = getFileExtension(file);
        return ext === 'pdf';
    };
    
    const getFileTypeIcon = (file) => {
        const ext = getFileExtension(file);
        
        if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) {
            return "fa-solid fa-file-word text-blue-500";
        } else if (['xls', 'xlsx', 'ods', 'csv'].includes(ext)) {
            return "fa-solid fa-file-excel text-green-500";
        } else if (['ppt', 'pptx', 'odp'].includes(ext)) {
            return "fa-solid fa-file-powerpoint text-red-500";
        } else if (ext === 'pdf') {
            return "fa-solid fa-file-pdf text-red-600";
        } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
            return "fa-solid fa-file-zipper text-yellow-600";
        } else if (['txt', 'md'].includes(ext)) {
            return "fa-solid fa-file-lines text-gray-600";
        } else {
            return "fa-solid fa-file text-gray-500";
        }
    };
    
    const openFileInNewTab = (file) => {
        if (!file) {
            console.error('Nessun file valido');
            return;
        }
        
        // Se file è un oggetto con URL
        if (typeof file === 'object' && file.url) {
            window.open(file.url, '_blank');
            return;
        }
        
        // A questo punto file dovrebbe essere una stringa
        const fileId = typeof file === 'string' ? file : '';
        if (!fileId) {
            console.error('Formato file non valido:', file);
            return;
        }
        
        // Controlla se l'ID del file contiene un'URL completa
        if (fileId.startsWith('http')) {
            window.open(fileId, '_blank');
            return;
        }
        
        // Se l'ID inizia con gs:// è un percorso su Google Cloud Storage
        if (fileId.startsWith('gs://')) {
            // Estraiamo il bucket e il percorso
            const gcsPath = fileId.replace('gs://', '');
            const [bucket, ...objectParts] = gcsPath.split('/');
            const objectPath = objectParts.join('/');
            
            // Creiamo l'URL pubblico di Google Cloud Storage
            const publicUrl = `https://storage.googleapis.com/${bucket}/${objectPath}`;
            window.open(publicUrl, '_blank');
            return;
        }
        
        // URL di base per i file caricati
        const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
        
        // Apre il file in una nuova scheda
        window.open(`${baseUrl}/uploads/${fileId}`, '_blank');
    };

    // Verifica se questo appuntamento è quello selezionato
    const isSelected = selectedAppointmentId && appointment._id === selectedAppointmentId;
    
    return (
        <div className="bg-gray-50 rounded p-2" style={{
            border: isSelected 
                ? `4px solid ${palette.primary}`
                : '1px solid #f3f4f6',
            boxShadow: isSelected ? `0 0 0 2px ${palette.primary}` : 'none',
            transition: 'all 0.2s ease-in-out',
            position: 'relative'
        }}>
            {/* Indicatore di selezione */}
            {isSelected && (
                <div style={{
                    position: 'absolute',
                    top: '-10px',
                    left: '-10px',
                    backgroundColor: palette.primary,
                    color: palette.textOnPrimary,
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    zIndex: 2,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}>
                    ✓
                </div>
            )}
            {/* Intestazione con data e ora e badge utenti */}
            <div className="flex justify-between items-center mb-3 p-2 rounded border" style={{ backgroundColor: '#EBEBEB' }}>
                <div className="flex flex-col">
                    {/* Riga data e orario */}
                    <div className="flex items-center">
                        <DynamicIcon iconClass="fa-solid fa-calendar-days text-green-600" />
                        <div className="ml-2 flex items-baseline gap-2">
                            <span className="text-lg font-semibold text-gray-700">
                                {formatDate(appointment.fromDate)}
                            </span>
                            <span className="text-sm text-gray-600">
                                {formatTime(appointment.fromDate)}-{formatTime(appointment.toDate)}
                            </span>
                        </div>
                    </div>
                    {/* Riga riferimenti commessa */}
                    {(appointment.jobCode || appointment.jobTypeLabel) && (
                        <div className="flex items-center mt-1">
                            <DynamicIcon iconClass="fa-solid fa-briefcase text-blue-600" />
                            <div className="ml-2 text-sm text-gray-600">
                                {appointment.jobCode && <span className="font-medium">{appointment.jobCode}</span>}
                                {appointment.jobCode && appointment.jobTypeLabel && <span className="mx-1">-</span>}
                                {appointment.jobTypeLabel && <span>{appointment.jobTypeLabel}</span>}
                                {/* Codice assistenza se presente */}
                                {appointment.assistanceRequestCode && (
                                    <span className="ml-2 text-orange-600">
                                        <DynamicIcon iconClass="fa-solid fa-headset" /> {appointment.assistanceRequestCode}
                                    </span>
                                )}
                                {/* Codice manutenzione se presente */}
                                {appointment.maintenanceCode && (
                                    <span className="ml-2 text-purple-600">
                                        <DynamicIcon iconClass="fa-solid fa-wrench" /> {appointment.maintenanceCode}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Badge utenti con ore */}
                <div className="flex flex-col items-end gap-2">
                    {appointment.users && appointment.users.length > 0 && (
                        <div className="flex flex-wrap gap-1 ml-2">
                            {(() => {
                                // Funzione per determinare il colore del testo in base al background color
                                const getTextColor = (bgColor) => {
                                    if (!bgColor || bgColor === '') return '#ffffff';
                                    const hex = bgColor.replace('#', '');
                                    const r = parseInt(hex.substring(0, 2), 16);
                                    const g = parseInt(hex.substring(2, 4), 16);
                                    const b = parseInt(hex.substring(4, 6), 16);
                                    return (r * 0.299 + g * 0.587 + b * 0.114) > 140 ? '#000000' : '#ffffff';
                                };

                                const calcUserTime = (userId) => {
                                    const data = appointment.closingDetails
                                        ?.filter(detail => detail.userId === userId)
                                        ?.reduce((total, detail) => ({
                                            totalHours: total.totalHours + (parseInt(detail.workHours, 10) || 0),
                                            totalMinutes: total.totalMinutes + (parseInt(detail.workMinutes, 10) || 0)
                                        }), { totalHours: 0, totalMinutes: 0 }) || { totalHours: 0, totalMinutes: 0 };
                                    return {
                                        hours: data.totalHours + Math.floor(data.totalMinutes / 60),
                                        minutes: data.totalMinutes % 60
                                    };
                                };

                                // Utenti non assegnati ma con ore nei closingDetails
                                const assignedUserIds = appointment.users?.map(u => u.userId) || [];
                                const unassignedWithHours = (appointment.closingDetails || [])
                                    .filter(cd => !assignedUserIds.includes(cd.userId) && ((cd.workHours || 0) > 0 || (cd.workMinutes || 0) > 0))
                                    .reduce((acc, cd) => {
                                        if (!acc.find(u => u.userId === cd.userId)) acc.push(cd);
                                        return acc;
                                    }, []);

                                return (
                                    <>
                                        {appointment.users.map((user, index) => {
                                            const time = calcUserTime(user.userId);
                                            return (
                                                <span
                                                    key={user.userId || index}
                                                    className="px-2 py-0.5 text-xs rounded-full flex items-center gap-1"
                                                    style={{
                                                        backgroundColor: user.userColor || '#6B7280',
                                                        color: getTextColor(user.userColor)
                                                    }}
                                                >
                                                    <DynamicIcon iconClass="fa-solid fa-user" />
                                                    {user.userLabel}
                                                    {(time.hours > 0 || time.minutes > 0) && (
                                                        <span className="ml-1 font-semibold">
                                                            ({time.hours}h {time.minutes > 0 ? `${time.minutes}m` : ''})
                                                        </span>
                                                    )}
                                                </span>
                                            );
                                        })}
                                        {unassignedWithHours.map((cd) => {
                                            const time = calcUserTime(cd.userId);
                                            return (
                                                <span
                                                    key={`unassigned_${cd.userId}`}
                                                    className="px-2 py-0.5 text-xs rounded-full flex items-center gap-1"
                                                    style={{ backgroundColor: '#fef3cd', color: '#856404', border: '1px dashed #856404' }}
                                                    title="Ore caricate da ufficio - collaboratore non assegnato"
                                                >
                                                    <DynamicIcon iconClass="fa-solid fa-building" />
                                                    {cd.userLabel}
                                                    {(time.hours > 0 || time.minutes > 0) && (
                                                        <span className="ml-1 font-semibold">
                                                            ({time.hours}h {time.minutes > 0 ? `${time.minutes}m` : ''})
                                                        </span>
                                                    )}
                                                </span>
                                            );
                                        })}
                                    </>
                                );
                            })()}
                        </div>
                    )}
                    
                    {/* Icona per caricare ore - solo per pianificazioni commessa, non per appuntamenti generici */}
                    {!appointment.isGenericAppointment && appointment.jobId && (
                        <button 
                            className="p-2 hover:bg-gray-200 rounded-full transition-colors duration-200"
                            title="Carica ore"
                            onClick={(e) => {
                                e.stopPropagation();
                                dispatch(closeModal());
                                navigate(`/loadEmployeeHours?appointmentId=${appointment._id}`);
                            }}
                        >
                            <DynamicIcon iconClass="fa-solid fa-clock text-green-600" />
                        </button>
                    )}
                    
                    {/* Icona per mostrare dettaglio */}
                    <button 
                        className="p-2 hover:bg-gray-200 rounded-full transition-colors duration-200"
                        title={expandedDetails[appointment._id] ? "Nascondi dettaglio" : "Mostra dettaglio appuntamento"}
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpandedDetails(prev => ({
                                ...prev,
                                [appointment._id]: !prev[appointment._id]
                            }));
                        }}
                    >
                        <DynamicIcon iconClass={expandedDetails[appointment._id] ? "fa-solid fa-eye-slash text-gray-600" : "fa-solid fa-eye text-blue-600"} />
                    </button>
                </div>
            </div>
            
            {/* Note dell'appuntamento - sempre visibili se presenti */}
            {appointment.notes && (
                <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-1">
                        <DynamicIcon iconClass="fa-solid fa-sticky-note text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">Note Appuntamento:</span>
                    </div>
                    <p className="text-sm text-blue-900 whitespace-pre-line">{appointment.notes}</p>
                </div>
            )}
            
            {/* Closing Details - Visibili solo se espansi */}
            {expandedDetails[appointment._id] && (
                <>
                    {appointment.closingDetails && appointment.closingDetails.length > 0 ? (
                        <div className="mt-2">
                            {appointment.closingDetails.map((detail, idx) => (
                                <div key={`${appointment._id}_${detail.userId || idx}`} className="mb-3 p-3 bg-gray-50 rounded border border-gray-200">
                                    <div className="font-semibold text-base text-gray-700 flex items-center gap-2">
                                        {detail.userLabel}
                                        {!appointment.users?.some(u => u.userId === detail.userId) && (
                                            <span className="inline-block px-1.5 py-0.5 rounded text-xs font-normal" style={{ backgroundColor: '#fef3cd', color: '#856404' }}>
                                                caricate da ufficio
                                            </span>
                                        )}
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-2 mt-2">
                                        <div className="flex items-center gap-2">
                                            <DynamicIcon iconClass="fa-solid fa-clock text-gray-600" />
                                            <span className="text-sm font-medium">{detail.workHours || 0}h {detail.workMinutes || 0}m</span>
                                        </div>
                                        {detail.vehicleLabel && (
                                            <div className="flex items-center gap-2">
                                                <DynamicIcon iconClass="fa-solid fa-truck text-gray-500" />
                                                <span className="text-sm font-medium">{detail.vehicleLabel}</span>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Note con anteprima */}
                                    {detail.notes && (
                                        <div className="mt-3">
                                            <div className="flex items-center gap-1 mb-1">
                                                <DynamicIcon iconClass="fa-solid fa-comment text-gray-500" />
                                                <span className="text-sm font-medium">Note:</span>
                                            </div>
                                            <div className="bg-white p-2 rounded border border-gray-200 text-sm">
                                                {!expandedNotes[`${appointment._id}_${detail.userId}`] ? (
                                                    <>
                                                        <span>{detail.notes.split(' ').slice(0, 30).join(' ')}
                                                        {detail.notes.split(' ').length > 30 ? '...' : ''}</span>
                                                        {detail.notes.split(' ').length > 30 && (
                                                            <button 
                                                                className="ml-1 text-blue-500" 
                                                                onClick={() => toggleNotes(appointment._id, detail.userId)}
                                                            >
                                                                Mostra tutto
                                                            </button>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        <span>{detail.notes}</span>
                                                        <button 
                                                            className="ml-1 text-blue-500" 
                                                            onClick={() => toggleNotes(appointment._id, detail.userId)}
                                                        >
                                                            Nascondi
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Lista allegati con AttachmentsViewer - Solo quando espanso */}
                                    {detail.files && detail.files.length > 0 && (
                                        <div className="mt-3">
                                            <AttachmentsViewer 
                                                attachments={detail.files} 
                                                title={`Allegati (${detail.files.length})`}
                                                showPreview={true}
                                                downloadAllHandler={() => downloadAllFiles(appointment._id, detail)}
                                                downloadInProgress={downloadingFiles[`${appointment._id}_${detail.userId}`]}
                                                lazyLoad={true}
                                            />
                                        </div>
                                    )}
                                    
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="mt-2 p-2 text-center">
                            <div className="text-gray-500 italic text-xs">
                                Nessuna chiusura trovata
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
